import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, combineLatest, filter, map, merge, take } from 'rxjs';
import { Select } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { AppDataState } from '@app/shared/stores/app-data.state';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { DataGridColumnDef, GetDriverDataSearchByList } from './driver-data.constant';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { GetDriverData } from '@app/shared/actions/app-data.action';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { DriverDataDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/driver-data-data-grid-cell.component';
import { Driver } from '@app/shared/classes/driver';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { DriverDataEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/driver-data-edit-modal-dialog/driver-data-edit-modal-dialog.component';

@Component({
  selector: 'app-driver-data',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DataGridComponent,
    TranslateModule,
    SmartSearchFormComponent,
    LoadingSpinnerComponent,
    DriverDataDataGridCell,
    MessageDataComponent
  ],
  templateUrl: './driver-data.component.html',
  styleUrl: './driver-data.component.css'
})
export class DriverDataComponent extends PageBasedComponent {
  formPath = NgxsFormPath.DRIVER_DATA_SEARCH_FORM;
  dataGridColumnDef = DataGridColumnDef;
  dataSource$: Observable<Driver[]>;
  selectedItems: Driver[] = [];

  driverDataSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentDriverDataSearchBy) currentDriverDataSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_DRIVER_DATA)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_DRIVER_DATA)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_DRIVER_DATA)) response$: Observable<WebApiResponse<Driver[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_DRIVER_DATA)) errorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_DRIVER_DATA;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentDriverDataSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    this.driverDataSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$]).pipe(map((c) => GetDriverDataSearchByList(c)));
  }

  rowSelected = (data: Driver[]) => {
    this.selectedItems = data ?? [];
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetDriverData());
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_DRIVER_DATA;
    this.store.dispatch(new GetDriverData(info));
  }

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetDriverData(null));
  }

  onEdit = () => {
    this.dialog.open(DriverDataEditModalDialogComponent, {
      width: '700px',
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true
    });
  }
}
